import React, { KeyboardEvent, useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'

import { ToastType, useToast } from '../../../hooks/useToast'
import { useChangePriceForPartsMutation } from '../../../modules/store/datahubApi/datahubApi'
import { Part, PartListSearchParams } from '../../../types/part/partTypes'
import Loader from '../../_atoms/Loader/Loader'
import OverlayWithText from '../../_atoms/OverlayWithText/OverlayWithText'
import PartSwitch from '../PartSwitch/PartSwitch'

import styles from './styles.module.scss'

type PriceMode = 'percent' | 'fixed'
type PercentDirection = 'increase' | 'decrease'

const MODE_OPTIONS = ['Изменить на %', 'Установить цену']
const DIRECTION_OPTIONS = ['Увеличить', 'Уменьшить']

interface Props {
    isOpen: boolean
    closeModal: () => void
    parts: Part[]
    confirmChanges: () => void
    partListSearchParams?: PartListSearchParams
}

export const ModalChangePrice = ({
    isOpen,
    closeModal,
    parts,
    confirmChanges,
    partListSearchParams,
}: Props) => {
    const [mode, setMode] = useState<PriceMode>('percent')
    const [percentDirection, setPercentDirection] = useState<PercentDirection>('increase')
    const [percentValue, setPercentValue] = useState<number>(10)
    const [fixedPrice, setFixedPrice] = useState<number>(0)
    const [noChanges, setNoChanges] = useState(false)
    const [fetchChangePrice, { isLoading, error, data }] = useChangePriceForPartsMutation()

    const onCloseModal = () => {
        closeModal()
    }

    const handleNumericKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!((e.key >= '0' && e.key <= '9') || e.key === 'Backspace' || e.key === 'Delete' || e.key.includes('Arrow'))) {
            e.preventDefault()
        }
    }

    const isSubmitDisabled =
        mode === 'percent'
            ? !percentValue || percentValue <= 0
            : fixedPrice < 0

    const confirm = async () => {
        const signedPercentValue =
            mode === 'percent'
                ? percentDirection === 'increase'
                    ? percentValue
                    : -percentValue
                : undefined

        fetchChangePrice({
            partsId: parts.map(part => part.id),
            params: partListSearchParams,
            mode,
            percentValue: signedPercentValue,
            fixedPrice: mode === 'fixed' ? fixedPrice : undefined,
        })
    }

    useEffect(() => {
        if (!data) {
            return
        }

        if (data.changedCount === 0) {
            setNoChanges(true)
            return
        }

        confirmChanges()
        closeModal()
    }, [closeModal, confirmChanges, data])

    useToast({ isTriggered: !!error, type: ToastType.ERROR, message: 'Ошибка изменения цен' })
    useToast({
        isTriggered: !!data && data.changedCount > 0,
        type: ToastType.SUCCESS,
        message: `Цены успешно изменены (${data?.changedCount ?? 0} запч.)`,
    })
    useToast({
        isTriggered: noChanges,
        type: ToastType.ERROR,
        message: 'Нет изменений — цены уже соответствуют выбранным параметрам',
    })

    return (
        <Modal show={isOpen} onHide={onCloseModal} centered dialogClassName={styles.modal}>
            {isLoading && (
                <OverlayWithText backgroundBootstrapColor={'bg-secondary'}>
                    <Loader diameterInPx={100} thicknessInPx={10} />
                </OverlayWithText>
            )}
            <Modal.Header closeButton>
                <Modal.Title>
                    <b>Изменить цены</b>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <div className={styles.switchGroup}>
                        <PartSwitch
                            options={MODE_OPTIONS}
                            activeOption={mode === 'percent' ? 0 : 1}
                            onChangeValue={index => setMode(index === 0 ? 'percent' : 'fixed')}
                        />
                    </div>

                    {mode === 'percent' && (
                        <>
                            <div className={styles.switchGroup}>
                                <PartSwitch
                                    options={DIRECTION_OPTIONS}
                                    activeOption={percentDirection === 'increase' ? 0 : 1}
                                    onChangeValue={index =>
                                        setPercentDirection(index === 0 ? 'increase' : 'decrease')
                                    }
                                />
                            </div>
                            <Form.Group className="mb-0">
                                <Form.Label>Процент</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={percentValue.toLocaleString('ru-RU')}
                                    onKeyDown={handleNumericKeyDown}
                                    onChange={e => setPercentValue(+e.target.value.replace(/[^0-9]/g, '') || 0)}
                                    placeholder="10"
                                />
                            </Form.Group>
                        </>
                    )}

                    {mode === 'fixed' && (
                        <Form.Group className="mb-0">
                            <Form.Label>Новая цена</Form.Label>
                            <Form.Control
                                type="text"
                                value={fixedPrice.toLocaleString('ru-RU')}
                                onKeyDown={handleNumericKeyDown}
                                onChange={e => setFixedPrice(+e.target.value.replace(/[^0-9]/g, '') || 0)}
                                placeholder="0"
                            />
                        </Form.Group>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCloseModal}>
                    Отмена
                </Button>
                <Button variant="primary" onClick={confirm} disabled={isSubmitDisabled || isLoading}>
                    Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
